import {Injectable} from '@angular/core';

import {BehaviorSubject, Observable} from 'rxjs';
import {isDefined} from '../commons/commons';

// different modes for the menu
export enum MenuMode {
    ENERGY_COMMUNITY
}

// different operating modes for the entity tree
export enum TreeMode {
    ENERGY_COMMUNITY, // energy-community mode: only root nodes and communities shown (in this order)
    EDIT // all nodes shown; nodes can be edited (rename, tag, drag, ...)
}

const DEFAULT_MENU_MODE = MenuMode.ENERGY_COMMUNITY;
const DEFAULT_TREE_MODE = TreeMode.ENERGY_COMMUNITY;

/**
 * Service that allows shared access to the overall "menu-related" capabilities of the application,
 * including the entity tree's status and "mode" (such as selectable nodes) and the presence/absence
 * of the menu.
 */
@Injectable()
export class MenuService {

    private showMenuSubj: BehaviorSubject<boolean>;
    private menuEnabledSubj: BehaviorSubject<boolean>;
    private menuModeSubj: BehaviorSubject<MenuMode>;
    private showTreeSubj: BehaviorSubject<boolean>;
    private treeEnabledSubj: BehaviorSubject<boolean>;
    private treeModeSubj: BehaviorSubject<TreeMode>;

    constructor() {
        this.showMenuSubj = new BehaviorSubject<boolean>(false);
        this.menuEnabledSubj = new BehaviorSubject<boolean>(true);
        this.menuModeSubj = new BehaviorSubject<MenuMode>(DEFAULT_MENU_MODE);
        this.showTreeSubj = new BehaviorSubject<boolean>(true);
        this.treeEnabledSubj = new BehaviorSubject<boolean>(false);
        this.treeModeSubj = new BehaviorSubject<TreeMode>(DEFAULT_TREE_MODE);
    }

    // <menu>

    get menuShowStatus(): Observable<boolean> {
        return this.showMenuSubj.asObservable();
    }

    get menuEnabledStatus(): Observable<boolean> {
        return this.menuEnabledSubj.asObservable();
    }

    get menuMode(): Observable<MenuMode> {
        return this.menuModeSubj.asObservable();
    }

    showMenu() {
        this.showMenuSubj.next(true);
    }

    hideMenu() {
        this.showMenuSubj.next(false);
    }

    toggleMenu() {
        this.showMenuSubj.next(!this.showMenuSubj.value);
    }

    enableMenu(mode?: MenuMode) {
        this.menuModeSubj.next(isDefined(mode) ? mode : DEFAULT_MENU_MODE);
        this.menuEnabledSubj.next(true);
    }

    disableMenu() {
        this.menuEnabledSubj.next(false);
    }

    // </menu>

    // <entity-tree>

    get treeShowStatus(): Observable<boolean> {
        return this.showTreeSubj.asObservable();
    }

    get treeEnabledStatus(): Observable<boolean> {
        return this.treeEnabledSubj.asObservable();
    }

    get treeMode(): Observable<TreeMode> {
        return this.treeModeSubj.asObservable();
    }

    showTree() {
        this.showTreeSubj.next(true);
    }

    hideTree() {
        this.showTreeSubj.next(false);
    }

    toggleTree() {
        this.showTreeSubj.next(!this.showTreeSubj.value);
    }

    enableTree() {
        this.treeEnabledSubj.next(true);
    }

    disableTree() {
        this.treeEnabledSubj.next(false);
    }

    setTreeMode(mode?: TreeMode) {
        this.treeModeSubj.next(isDefined(mode) ? mode : DEFAULT_TREE_MODE);
    }

    // </entity-tree>
}
